"use client";

import { CalendarClock, CheckCircle2, MapPin, User } from "lucide-react";

import { SummaryPanel, SummaryRow } from "@/src/components/online-scheduling/summary";
import { formatLocality, type OnlineSchedulingLocation } from "@/src/components/online-scheduling/types";
import { Button } from "@/src/components/shadcn/ui/button";
import { H2, Muted } from "@/src/components/typography";
import { formatSlotDateTime } from "@/src/lib/scheduling/slot-display";

export function SuccessStep({
  location,
  slotStart,
  providerDisplayName,
  patientName,
  onBookAnother,
}: {
  location: OnlineSchedulingLocation;
  slotStart: string;
  providerDisplayName: string | null;
  patientName: string;
  onBookAnother: () => void;
}) {
  const locality = formatLocality(location);

  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
        <CheckCircle2 className="h-8 w-8 text-success" />
      </div>

      <div className="space-y-2">
        <H2 className="text-foreground">You&apos;re all set, {patientName}!</H2>
        <Muted className="block">Your appointment is confirmed.</Muted>
      </div>

      <SummaryPanel>
        <SummaryRow icon={<CalendarClock className="h-4 w-4" />} label="Appointment">
          <p className="font-medium">{formatSlotDateTime(slotStart)}</p>
          {providerDisplayName && <p className="text-muted-foreground">with {providerDisplayName}</p>}
        </SummaryRow>
        <SummaryRow icon={<MapPin className="h-4 w-4" />} label="Location">
          <p className="font-medium">{location.displayName}</p>
          {location.addressLine1 && <p className="text-muted-foreground">{location.addressLine1}</p>}
          {locality && <p className="text-muted-foreground">{locality}</p>}
        </SummaryRow>
        <SummaryRow icon={<User className="h-4 w-4" />} label="Patient">
          <p className="font-medium">{patientName}</p>
          <p className="text-muted-foreground">Arrive 15 minutes early with a photo ID and insurance card.</p>
        </SummaryRow>
      </SummaryPanel>

      <Button variant="outline" onClick={onBookAnother}>
        Book another appointment
      </Button>
    </div>
  );
}
